import { FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl, MessageValue } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';

import {
    getTradeBuyerProtectionDuration,
    getTradeBuyerProtectionTier,
} from '../helpers/tradeBuyerProtectionHelper';

import { PdpSharedBuyerProtectionGuarantee_user$key } from './__generated__/PdpSharedBuyerProtectionGuarantee_user.graphql';

import styles from './PdpSharedBuyerProtection.scss';

const userFragment = graphql`
    fragment PdpSharedBuyerProtectionGuarantee_user on User {
        isVerifiedTrade
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                    childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                        code
                    }
                }
            }
        }
    }
`;

const copy = defineMessages({
    readMoreLinkA11y: {
        id: 'PdpSharedBuyerProtectionGuarantee.readMoreLinkA11y',
        defaultMessage: 'Learn more about buyer protection',
    },
});

export const PdpSharedBuyerProtectionGuarantee: FC<{
    user: PdpSharedBuyerProtectionGuarantee_user$key | null;
}> = ({ user: userRef }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);

    const tradeUserRewardsProgram = user?.tradeFirm?.rewards?.currentProgram;
    const tradeProtectionTierCode = tradeUserRewardsProgram?.programInfo?.code;
    const tradeProtectionDurationCode = tradeUserRewardsProgram?.childPrograms?.[0]?.code;
    const showTradeRewardsCopy = !!tradeProtectionDurationCode;

    const readMoreHref = user?.isVerifiedTrade ? '/info/trade/promise/' : '/about/promise/';

    const learnMoreHandler: MessageValue = useCallback(
        chunks => (
            <Link
                href={readMoreHref}
                ariaLabel={intl.formatMessage(copy.readMoreLinkA11y)}
                target="_blank"
                dataTn="buyer-protection-link"
            >
                {chunks}
            </Link>
        ),
        [readMoreHref, intl]
    );

    return (
        <div className={styles.copy}>
            <div className={styles.title} data-tn="buyer-protection-title">
                <FormattedMessage
                    id="PdpSharedBuyerProtectionGuarantee.title"
                    defaultMessage="1stDibs Buyer Protection Guaranteed"
                />
            </div>
            <div data-tn="buyer-protection-message">
                {showTradeRewardsCopy ? (
                    <FormattedMessage
                        id="PdpSharedBuyerProtectionGuarantee.tradeRewardsMessage"
                        defaultMessage="If your item arrives not as described, we’ll work with you and the seller to make it right. As a {tier}, you get {days} to report any issues you have with the order. <a>Learn More</a>"
                        values={{
                            tier: (
                                <span className={styles.messageBold}>
                                    {getTradeBuyerProtectionTier(tradeProtectionTierCode)}
                                </span>
                            ),
                            days: (
                                <span className={styles.messageBold}>
                                    {getTradeBuyerProtectionDuration(tradeProtectionDurationCode)}
                                </span>
                            ),
                            a: learnMoreHandler,
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="PdpSharedBuyerProtectionGuarantee.message"
                        defaultMessage="If your item arrives not as described, we’ll work with you and the seller to make it right. <a>Learn More</a>"
                        values={{ a: learnMoreHandler }}
                    />
                )}
            </div>
        </div>
    );
};
